/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { Icon, Icons } from 'components/Icon';
import { BondType } from 'apps/user/types/on-boarding.type';
import { useCreateBond } from 'common/queries-and-mutations/bond';
import { InfoModal } from 'components/Modal';
import CustomSelect from 'components/Form/CustomSelect';
import { countriesCurrency } from 'constants/countries-currency';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddBondModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useCreateBond();

  const formik = useFormik({
    initialValues: {
      bonds: [
        {
          bondId: null,
          issuerName: '',
          description: '',
          coupon: '',
          maturityDate: '',
          currency: '',
          faceValue: '',
          ISINNo: '',
          unit: ''
        }
      ]
    },
    validationSchema: Yup.object({
      bonds: Yup.array().of(
        Yup.object().shape({
          issuerName: Yup.string()
            .required('Issuer name is required')
            .nullable(),
          description: Yup.string()
            .required('Description is required')
            .nullable(),
          coupon: Yup.string().required('Coupon is required').nullable(),
          maturityDate: Yup.date()
            .required('Maturity Date is required')
            .nullable(),
          currency: Yup.string().required('Currency is required').nullable(),
          faceValue: Yup.string().required('Face value is required').nullable(),
          ISINNo: Yup.string().required('ISIN No is required').nullable(),
          unit: Yup.string().required('Unit is required').nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBondSubmit(values);
    }
  });

  const onBondSubmit = (formValue: BondType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(<Alert message="Bond Successfully Added" type="success" />);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      <InfoModal width="w-[60%]" className="h-fit rounded-md">
        <div className="flex flex-row items-center justify-between border-b p-3 pl-6">
          <p className="text-2xl font-bold text-[#353535]">
            Add Bond Information
          </p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="p-8"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.bonds.map((bond, index) => (
            <div key={index} className="my-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].issuerName`}
                    placeholder=""
                    value={bond.issuerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Name of Issuer"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).issuerName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].description`}
                    placeholder=""
                    value={bond.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Description"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).description
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].coupon`}
                    placeholder=""
                    value={bond.coupon}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Coupon/Interest Rate"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).coupon
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    type="date"
                    name={`bonds[${index}].maturityDate`}
                    placeholder=""
                    value={bond.maturityDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Maturity Date (DD/MM/YYYY)"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).maturityDate
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={countriesCurrency.map(country => ({
                        value: country.currency,
                        label: `${country.name} (${country.currency})`
                      }))}
                      selectedOption={{
                        value: bond.currency,
                        label: bond.currency
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `bonds[${index}].currency`,
                          option?.value
                        );
                      }}
                      label="Currency"
                      name="currency"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).currency
                            : ''
                          : ''
                      }
                    />
                  </div>
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].faceValue`}
                    placeholder=""
                    value={bond.faceValue}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Face Value"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).faceValue
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="-mt-5 grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].ISINNo`}
                    placeholder=""
                    value={bond.ISINNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="ISIN No."
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).ISINNo
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`bonds[${index}].unit`}
                    placeholder=""
                    value={bond.unit}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="How Many Units"
                    error={
                      formik.touched.bonds
                        ? formik.errors.bonds?.[index] &&
                          formik.errors.bonds.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.bonds[index])
                            ).unit
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-10 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Add New Bond"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </InfoModal>
    </div>
  );
}
